import {
  Backdrop,
  Box,
  type BoxProps,
  type Breakpoint,
  Divider,
  Modal,
  type ModalProps,
  Stack,
  Typography,
} from '@mui/material';
import { CloseIcon } from 'components/icons/CloseIcon';
import type { FC, ReactNode } from 'react';

export interface Props extends Omit<ModalProps, 'open' | 'children'> {
  width?: string | Partial<Record<Breakpoint, string>>;
  containerStyle?: BoxProps['sx'];
  open?: boolean;
  shouldDisplayDivider?: boolean;
  children: ReactNode;
}

export const BasicModal: FC<Props> = ({
  children,
  title,
  onClose,
  open = true,
  width = '600px',
  shouldDisplayDivider = true,
  containerStyle,
  ...other
}) => (
  <Modal
    role="dialog"
    closeAfterTransition
    slots={{ backdrop: Backdrop }}
    slotProps={{
      backdrop: {
        timeout: 500,
        sx: { background: 'none', backgroundColor: 'primary.light', opacity: '0.2 !important' },
      },
    }}
    {...other}
    onClose={onClose}
    open={open}
  >
    <Box
      aria-labelledby="modal-title"
      sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        backgroundColor: 'white',
        border: '1px solid transparent',
        borderRadius: '0.5rem',
        outline: '0',
        maxHeight: '95vh',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: 24,
        '&::-webkit-scrollbar': { display: 'none' },
        width,
        ...containerStyle,
      }}
    >
      <Stack direction="row" justifyContent={onClose ? 'space-between' : 'center'} padding="20px">
        {title && (
          <Typography id="modal-title" variant="h5" color="GrayText" mr={2}>
            {title}
          </Typography>
        )}
        {onClose && (
          <CloseIcon
            sx={{ cursor: 'pointer', color: 'gray' }}
            width={20}
            height={20}
            onClick={(e) => onClose(e, 'backdropClick')}
          />
        )}
      </Stack>
      {shouldDisplayDivider && <Divider />}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          scrollbarWidth: 'none',
          padding: '20px',
        }}
      >
        {children}
      </Box>
    </Box>
  </Modal>
);
